
import React from "react";

interface PdfSettings {
  company_name?: string;
  header_text?: string;
  footer_text?: string;
  header_image_url?: string;
  font_family?: string;
  font_size_body?: number;
  font_size_footnote?: number;
  margin_top?: number;
  margin_bottom?: number;
  margin_left?: number;
  margin_right?: number;
  line_spacing?: number;
  paragraph_indent?: number;
  page_numbering_enabled?: boolean;
  page_numbering_position?: string;
  lawyer_name?: string;
  oab_number?: string;
  oab_state?: string;
  include_lawyer_signature?: boolean;
}

interface PrintableDocumentProps {
  document: any;
  content: string;
  pdfSettings: PdfSettings | null;
}

const PrintableDocument = React.forwardRef<HTMLDivElement, PrintableDocumentProps>(
  ({ document, content, pdfSettings }, ref) => {
    const settings = pdfSettings || {};

    return (
      <div ref={ref} style={{ 
        fontFamily: settings.font_family || 'Times New Roman, serif',
        fontSize: `${settings.font_size_body || 12}pt`,
        lineHeight: settings.line_spacing || 1.5,
        color: 'black',
        backgroundColor: 'white',
        padding: `${settings.margin_top || 30}mm ${settings.margin_right || 20}mm ${settings.margin_bottom || 20}mm ${settings.margin_left || 30}mm`,
        minHeight: '297mm',
        width: '210mm',
        maxWidth: '210mm',
        margin: '0 auto'
      }}>
        {/* Header */}
        {(settings.company_name || settings.header_text || settings.header_image_url) && (
          <div style={{
            textAlign: 'center',
            marginBottom: '2em',
            paddingBottom: '1em',
            borderBottom: '1px solid #ccc'
          }}>
            {settings.header_image_url && (
              <img 
                src={settings.header_image_url} 
                alt="Logo" 
                style={{ maxHeight: '80px', marginBottom: '1em' }}
              />
            )}
            {settings.company_name && (
              <div style={{
                fontSize: '18pt',
                fontWeight: 'bold',
                marginBottom: '0.5em'
              }}>
                {settings.company_name}
              </div>
            )}
            {settings.header_text && (
              <div style={{
                fontSize: `${settings.font_size_footnote || 10}pt`,
                color: '#666'
              }}>
                {settings.header_text}
              </div>
            )}
          </div>
        )}

        {/* Document Title - renderizar apenas se não estiver no conteúdo */}
        {!content.includes('abnt-title') && (
          <div style={{
            textAlign: 'center',
            fontSize: '16pt',
            fontWeight: 'bold',
            margin: '2em 0',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            {document.title}
          </div>
        )}

        {/* Content */}
        <div style={{
          textAlign: 'justify',
          marginBottom: '2em'
        }}>
          <style>{`
            @media print {
              .abnt-format { font-family: "Times New Roman", serif; font-size: 12pt; line-height: 1.5; }
              .abnt-title { text-align: center; font-weight: bold; margin-bottom: 12pt; text-transform: uppercase; }
              .abnt-paragraph { text-align: justify; text-indent: 2cm; margin: 0 0 12pt 0; }
              .abnt-subtitle { font-weight: bold; margin: 12pt 0 6pt 0; }
              .abnt-list { margin: 6pt 0; padding-left: 2cm; }
            }
          `}</style>
          <div 
            className="abnt-format"
            dangerouslySetInnerHTML={{ __html: content }}
            style={{
              fontFamily: settings.font_family || 'Times New Roman, serif',
              fontSize: `${settings.font_size_body || 12}pt`,
              lineHeight: settings.line_spacing || 1.5,
              textAlign: 'justify'
            }} 
          />
        </div>

        {/* Signature Area */}
        <div style={{
          marginTop: '4em',
          textAlign: 'center'
        }}>
          {settings.include_lawyer_signature && (settings.lawyer_name || settings.oab_number) ? (
            <>
              <div style={{
                borderTop: '1px solid #000',
                width: '300px',
                margin: '0 auto 1em',
                paddingTop: '0.5em'
              }}></div>
              {settings.lawyer_name && (
                <div>{settings.lawyer_name}</div>
              )}
              {(settings.oab_number || settings.oab_state) && (
                <div>OAB/{settings.oab_state || 'XX'} {settings.oab_number || 'XXXXXX'}</div>
              )}
            </>
          ) : (
            <>
              <div style={{
                borderTop: '1px solid #000',
                width: '300px',
                margin: '0 auto 1em',
                paddingTop: '0.5em'
              }}></div>
              <div>Assinatura</div>
              <div>Data: {new Date().toLocaleDateString('pt-BR')}</div>
            </>
          )}
        </div>

        {/* Footer */}
        {settings.footer_text && (
          <div style={{
            marginTop: '4em',
            textAlign: 'center',
            fontSize: `${settings.font_size_footnote || 10}pt`,
            color: '#666',
            borderTop: '1px solid #ccc',
            paddingTop: '1em'
          }}>
            {settings.footer_text}
          </div>
        )}
      </div>
    );
  }
);

PrintableDocument.displayName = "PrintableDocument";

export default PrintableDocument;
